import img from "../assets/images/shutterstock_699442570-min.jpg";
import { useState } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";

export default function Reports() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    interests: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((interest) => interest !== value),
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
      Name: formData.name,
      "Email Address": formData.email,
      "Client Type": formData.clientType,
      "Interested in": formData.interests,
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      if (data?.success) {
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible.",
        });
      } else {
        setPopup({
          subject: "Something went wrong",
          message: "Please try again later.",
        })
      }
    } catch (err) {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  const seoConfig = pageSEO.reports();

  return (
    <>
      <SEO {...seoConfig} />

      <section className="bg-[#F8FAFC] min-h-screen pt-36 pb-24">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1400px] px-6"
        >
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <motion.div variants={fadeUp}>
                <div className="flex items-center gap-4">
                  <div className="h-px w-14 bg-harbour-accent" />
                  <p className="uppercase tracking-[0.4em] text-xs text-harbour-blue">
                    Exclusive Resources
                  </p>
                </div>

                <h1 className="mt-8 font-serif text-5xl lg:text-6xl leading-[0.95] text-harbour-navy">
                  Real Estate
                  <br />
                  Reports
                </h1>

                <p className="mt-8 text-lg leading-8 text-harbour-muted">
                  Receive valuable market updates, buying insights, and selling
                  strategies designed to help you make confident real estate
                  decisions.
                </p>

              </motion.div>

              <motion.div
                variants={fadeUp}
                className="relative mt-12 overflow-hidden rounded-[36px] shadow-2xl"
              >
                <img
                  src={img}
                  alt="Real Estate Reports"
                  className="h-[580px] w-full object-cover transition duration-700 hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-harbour-navy via-transparent to-transparent" />

                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <p className="uppercase tracking-[0.4em] text-xs text-white/70">
                    Market Intelligence
                  </p>

                  <h3 className="mt-4 font-serif text-3xl">
                    Stay ahead of the market
                  </h3>

                  <div className="mt-6 flex gap-8">
                    <div>
                      <p className="text-2xl font-serif">
                        24/7
                      </p>

                      <span className="text-xs uppercase tracking-widest text-white/70">
                        Updates
                      </span>
                    </div>

                    <div>
                      <p className="text-2xl font-serif">
                        Free
                      </p>

                      <span className="text-xs uppercase tracking-widest text-white/70">
                        Access
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              variants={fadeUp}
              className="lg:col-span-7"
            >
              <div className="rounded-[40px] bg-white p-8 lg:p-12 shadow-[0_25px_80px_rgba(15,23,42,0.08)]">
                <div className="mb-10">
                  <p className="uppercase tracking-[0.35em] text-xs text-harbour-blue">
                    Get Started
                  </p>

                  <h2 className="mt-4 font-serif text-4xl text-harbour-navy">
                    Request Your Reports
                  </h2>

                  <p className="mt-4 text-harbour-muted leading-7">
                    Tell us what information you are looking for and we will
                    send personalized resources directly to your inbox.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-widest text-slate-500">
                        Name*
                      </label>

                      <input
                        required
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Full Name"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-harbour-blue focus:bg-white"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-widest text-slate-500">
                        Email*
                      </label>

                      <input
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email Address"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-harbour-blue focus:bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-widest text-slate-500">
                      I am a*
                    </label>

                    <select
                      required
                      name="clientType"
                      value={formData.clientType}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none focus:border-harbour-blue"
                    >
                      <option value="">
                        Select
                      </option>
                      <option value="Buyer">
                        Buyer
                      </option>
                      <option value="Seller">
                        Seller
                      </option>
                      <option value="Renter">
                        Renter
                      </option>
                      <option value="Other">
                        Other
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-3 block text-xs uppercase tracking-widest text-slate-500">
                      Interested In
                    </label>

                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        "Buying guides",
                        "Selling guides",
                        "Choosing realtor",
                        "Renovations",
                      ].map((item) => (

                        <label
                          key={item}
                          className={`rounded-2xl border p-5 cursor-pointer transition ${formData.interests.includes(item)
                            ? "border-harbour-blue bg-slate-100"
                            : "border-slate-200 hover:border-harbour-blue"
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              value={item}
                              checked={formData.interests.includes(item)}
                              onChange={handleCheckboxChange}
                              className="h-5 w-5 accent-slate-900"
                            />

                            <span className="text-sm text-harbour-navy">
                              {item}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn-harbour"
                  >
                    Request Reports
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>


      {popup && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white p-10 text-center shadow-2xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-harbour-blue/10 text-2xl text-harbour-blue">
              ✓
            </div>
            <h3 className="mt-6 font-serif text-3xl text-harbour-navy">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-7 text-harbour-muted">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="btn-harbour mt-8"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
